import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  IconButton,
  List,
  ListItem,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { styled } from '@mui/material/styles';
import { PassengerIngestionServerConfiguration } from '../../../../data/models/passenger-ingestion-server-configuration.model';
import { useCommonTranslation } from '../../../../hooks/useCommonTranslation';
import { ClientState } from '../../../../data/models/client.model';
import { useIsSuperReaderUser } from '../../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'ClientServerConfigList';

const classes = {
  container: `${PREFIX}-container`,
  panelTools: `${PREFIX}-panelTools`,
  panelTitle: `${PREFIX}-panelTitle`,
  summary: `${PREFIX}-summary`,
  details: `${PREFIX}-details`,
  label: `${PREFIX}-label`,
  chips: `${PREFIX}-chips`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'stretch',
    gap: theme.spacing(1),
    margin: theme.spacing(2),
  },

  [`& .${classes.panelTools}`]: {
    width: '100%',
  },

  [`& .${classes.panelTitle}`]: {
    fontWeight: 700,
  },

  [`& .${classes.summary}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },

  [`& .${classes.details}`]: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
  },

  [`& .${classes.chips}`]: {
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

interface ClientServerConfigListProps {
  serverConfigs: PassengerIngestionServerConfiguration[];
  onEdit: (
    evt: any,
    serverConfig: PassengerIngestionServerConfiguration,
  ) => void;
  onActivate: (
    evt: any,
    serverConfig: PassengerIngestionServerConfiguration,
  ) => void;
  onDeactivate: (
    evt: any,
    serverConfig: PassengerIngestionServerConfiguration,
  ) => void;
  onDelete: (
    evt: any,
    serverConfig: PassengerIngestionServerConfiguration,
  ) => void;
}

export default function ClientServerConfigList({
  serverConfigs,
  onEdit,
  onActivate,
  onDeactivate,
  onDelete,
}: ClientServerConfigListProps) {
  const t = useCommonTranslation();

  const isSuperReaderUser = useIsSuperReaderUser();

  const handleActiveChange = (
    evt: any,
    serverConfig: PassengerIngestionServerConfiguration,
  ) => {
    if (evt.target.checked) {
      onActivate(evt, serverConfig);
    } else {
      onDeactivate(evt, serverConfig);
    }
  };

  return (
    <StyledBox className={classes.container}>
      {serverConfigs.map((serverConfig) => {
        return (
          <Accordion key={serverConfig.passengerEligibilitySettingUUID}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box className={classes.panelTools}>
                <Box className={classes.summary}>
                  <Typography className={classes.panelTitle}>
                    {serverConfig.eligibilityFileSourceURI}
                    {serverConfig.state === 'draft' && (
                      <Chip label={t('common.draft')} sx={{ ml: 1 }} />
                    )}
                  </Typography>
                  <Box>
                    {!isSuperReaderUser && (
                      <Tooltip title={t('client.editServerConfig')}>
                        <span>
                          <IconButton
                            data-testid="edit-config"
                            onClick={(evt) => onEdit(evt, serverConfig)}
                            color="primary"
                            size="large">
                            <EditOutlinedIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {!isSuperReaderUser && (
                      <Tooltip title={t('client.deleteServerConfig')}>
                        <span>
                          <IconButton
                            data-testid="delete-config"
                            disabled={serverConfig.state === ClientState.active}
                            onClick={(evt) => onDelete(evt, serverConfig)}
                            color="error"
                            size="large">
                            <DeleteForeverIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {!isSuperReaderUser && (
                      <Tooltip
                        title={
                          serverConfig.state === 'active'
                            ? t('pricingmodel.active')
                            : t('pricingmodel.inactive')
                        }>
                        <Switch
                          checked={serverConfig.state === 'active'}
                          onChange={(evt) =>
                            handleActiveChange(evt, serverConfig)
                          }
                          onClick={(evt) => evt.stopPropagation()}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box className={classes.details}>
                <List>
                  <ListItem>
                    <strong>{t('client.eligibilityFileSourceLocation')}</strong>
                    {': '}
                    {serverConfig.eligibilityFileSourceURI}
                  </ListItem>
                  <ListItem>
                    <strong>{t('client.eligibilityFileLegacyCopyPath')}</strong>
                    {': '}
                    {serverConfig.eligibilityFileLegacyCopyPath}
                  </ListItem>
                  <ListItem>
                    <strong>{t('client.ingestionCronJobBC')}</strong>
                    {': '}
                    {serverConfig.ingestionCronJobBC}
                  </ListItem>
                  <ListItem>
                    <strong>{t('client.effectiveStartDateTimeUTC')}</strong>
                    {': '}
                    {serverConfig.effectiveStartDate}
                  </ListItem>
                  <ListItem>
                    <strong>{t('client.effectiveEndDateTimeUTC')}</strong>
                    {': '}
                    {serverConfig.effectiveEndDate}
                  </ListItem>
                </List>
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </StyledBox>
  );
}
